import { AppName, CompanyName, getAppName } from 'modules/utils/ConfigUtils';

export const vyncaFaqCategories = [
  {
    id: 'heading1',
    title: 'Preparation and Planning'
  },
  {
    id: 'heading2',
    title: 'Completing Your Advance Directive Form'
  },
  {
    id: 'heading3',
    title: 'Sharing Your Document'
  },
  {
    id: 'heading4',
    title: 'Viewing and Changing Your Document'
  }
];

export const vyncaQuestionsList = [
  {
    categoryId: 'heading1',
    title: 'What are advance care planning decisions?',
    subtitle: [
      'Sometimes decisions must be made about the use of emergency treatments to keep you alive. Doctors can use several artificial or mechanical ways to do this. Decisions that might come up at this time relate to CPR (cardiopulmonary resuscitation), ventilator use, artificial nutrition, (tube feeding) and artificial hydration (IV, or intravenous fluids) and comfort care.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'Why is participating in advance care planning important?',
    subtitle: [
      'Participating in advance care planning gives you the opportunity to talk to your family or those closest to you, as well as your health care provider(s) about future medical care. After these discussions, your preferences can be written down and a trusted person identified to serve as your healthcare agent to make decisions for you if you are unable to do so.',
      'A written directive provides clarity and peace of mind, prepares your healthcare agent for the unexpected, and avoids confusion when the time comes where you cannot make decisions for yourself.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What is an advance directive?',
    subtitle: [
      {
        text: [
          'Once you determine who you would like to be your healthcare agent, your choice can be written down in a document called an advance directive. A written directive provides clarity and peace of mind, prepares your healthcare agent for the unexpected, and avoids confusion when the time comes where you cannot make decisions for yourself. Your directive can be as detailed as you would like or as simple as stating that you choose to let your healthcare agent decide.',
          '<br />',
          'Each state will call this document something different, however, the following terms are the most used:'
        ],
        bullets: [
          'Advance Directive for Healthcare',
          'Healthcare Advance Directive',
          'Healthcare Power of Attorney',
          'Medical Power of Attorney'
        ]
      }
    ]
  },
  {
    categoryId: 'heading1',
    title:
      'Can advance directives include information regarding cultural or religious beliefs that are important to me?',
    subtitle: [
      'Yes, these are your wishes. We suggest you consider the significant role that culture, beliefs, and values play in determining what you would want for future medical care and be sure to include this in your conversations and in your document.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'When will my advance directive be used?',
    subtitle: ['The advance directive will only be used when you cannot speak for yourself.']
  },
  {
    categoryId: 'heading1',
    title: 'Do doctors, nurses and hospitals have to follow my instructions?',
    subtitle: [
      'Healthcare providers are interested in honoring your wishes. They will make every effort to know and understand your wishes.',
      'There may be times when some of your preferences (i.e., music playing in the room, massage, essential oils, etc.) might not be possible.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What is the difference between an advance directive and a POLST/POST?',
    subtitle: [
      'A POLST (Physician Order for Life Sustaining Treatment) is a medical order that healthcare workers, including EMS, can base their actions on.',
      'A POST (Physician Orders for Scope of Treatment) is a set of medical instructions for healthcare workers to recognize patient treatment preferences for life-sustaining measures, such as CPR, breathing tubes, and feeding tubes.',
      'This portable medical order allows patients who are seriously ill or frail to voluntarily approach end-of-life planning in a way that honors and documents their wishes. It gives emergency responders a quick and clear reference document without having to look through an entire advance directive.',
      '<strong>A POLST/POST does not replace an advance directive.</strong> They work together. If you have a POLST form, you should be sure your advance directive reflects your same choices for care. A completed and signed POLST form is highly recommended if you wish to be DNR (Do Not Resuscitate) or no cardiopulmonary resuscitation (CPR).',
      'Your documents should be posted in a visible place in your home, often on the refrigerator, near your phone or at the bedside of a person who is mostly bedbound.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What is a Do Not Resuscitate (DNR)?',
    subtitle: [
      'DNR means “Do Not Resuscitate.” Some people do not want their heart to be re-started (resuscitated) if it stops. If you <strong>DO NOT</strong> want to be resuscitated or have CPR started on you, then you must complete a POLST form with your provider. (Refer to  <a href="navigate-to-question/heading1" categoryid="heading1" title="What is the difference between an advance directive and a POLST/POST?" >"What is the difference between an advance directive and a POLST/POST form"</a> for more information.)'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What is a healthcare agent?',
    subtitle: [
      "A healthcare agent is an individual who you choose to make medical decisions for you when you are unable to do so for yourself.  Examples of when you are unable to make your own medical decisions could be if you are unconscious, sedated, or if your ability to make decisions is compromised (e.g., dementia, Alzheimer's, delirium, etc.)"
    ]
  },
  {
    categoryId: 'heading1',
    title: 'Why do I need a healthcare agent?',
    subtitle: [
      'In a medical emergency, the people providing your care may not know you, your history, your goals or wishes for care. Having a trusted and strong healthcare agent who can speak on your behalf is important. They can advocate for you and help make complex decisions based on the conversations you have had with them before an event occurs.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What happens if I do not choose a healthcare agent or have an advance directive?',
    subtitle: [
      'If you become sick or injured and cannot communicate for yourself, health care providers will make treatment decisions based on what they believe is for your best interests. This could include treatments that you might or might not want. If you feel strongly about certain types of treatment that you would not want to receive, then an advance directive is an effective way to communicate your preferences.',
      'Each state has a default list of those who can be your decision maker. This default list could include a spouse/significant other, children, parents, siblings, and grandparents. The list will vary between states. You will want to research this for your state.',
      'If the care team is unable to contact a default decision maker, the providers on your care team will make decisions for you.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What types of decisions can a healthcare agent make on my behalf?',
    subtitle: [
      'Signing for release of your medical records and personal files.',
      'Applying for Medicare, Medicaid, or other programs for you.',
      'Arranging for your admission to a hospital, hospice, or nursing home.',
      'Giving consent or refusing medical services for you such as tests and surgery.',
      'Making the decision to request, take away, or not give medical treatments.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'Will my healthcare agent be able to make decisions for me that involve money?',
    subtitle: [
      'Not unless you complete a financial power of attorney form which grants your agent rights to make decisions for you about financial and property matters.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What should I consider as I select a healthcare agent?',
    subtitle: [
      {
        text: ['A healthcare agent needs to:'],
        bullets: [
          'Be willing to accept the role of your healthcare agent.',
          'Agree to talk to you about your healthcare choices and decisions.',
          'Be willing to implement and honor your choices, even if they do not agree with them.',
          'Be able to make decisions in a stressful and sometimes emergent, time-sensitive manner.'
        ]
      }
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What is the difference between a power of attorney and a healthcare agent?',
    subtitle: [
      'The main difference between a power of attorney and a healthcare agent is that a power of attorney may also be able to make financial decisions for you.',
      'In some states, a medical power of attorney and financial power of attorney are separate forms, while in others it is one document.',
      'You will want to find out what permission your state’s power of attorney form gives.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'Do I need a lawyer to fill out an advance directive?',
    subtitle: [
      'No. You can fill it out yourself. Once you have filled out the form, you will need to sign it in front of the proper witness to make it legal. If you are completing the form electronically, your witness will sign the form electronically. (Refer to, <a href="navigate-to-question/heading2" categoryid="heading2" title="Who cannot be a witness to an advance directive?">"Who cannot be a witness to an advance directive?"</a> for more information.)'
    ]
  },
  {
    categoryId: 'heading1',
    title:
      'What happens if I change my mind about what I want after I have completed the advance directive form?',
    subtitle: [
      'We recommend you review and update your advance directive annually, or if one of the “four D’s” occurs: diagnosis, deterioration, divorce, death of a healthcare agent. You can change your mind at any time and update or revoke your advance directive.',
      'This is always about what you want. The important thing to remember is to give your updated form to those who have access to your previously completed documents (such as a doctor, hospital, healthcare agent, or family).'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'Will my advance directive and preferences be honored if I go out of state?',
    subtitle: [
      'All 50 states (and the District of Columbia) generally recognize the use of advance directives. When you travel, you should bring copies of your health care documents or take a picture on your phone to make them easily accessible if an accident or unexpected illness occurs.'
    ]
  },
  {
    categoryId: 'heading1',
    title: 'What if English is not my primary language?',
    subtitle: [
      'If English is not your primary language, please ask your doctor to provide an interpreter to assist you in understanding and completing your advance directive.'
    ]
  },
  {
    categoryId: 'heading1',
    title: `Why is ${CompanyName} Partnering with Vynca?`,
    subtitle: [
      `${CompanyName} has partnered with Vynca Health to deliver a seamless way for you to complete and communicate your advance directive to your loved ones, healthcare providers, and others who you choose. Advance directives completed in the Vynca portal are automatically connected to your medical record.`
    ]
  },
  {
    categoryId: 'heading2',
    title: 'Overview of the online process.',
    subtitle: [
      'Have a discussion with the person you want as your healthcare agent.',
      'Complete the form electronically.',
      'Share your electronic form with your healthcare agent.'
    ]
  },
  {
    categoryId: 'heading2',
    title: 'How do I access the Vynca Portal?',
    subtitle: [
      `Click on the advance directive card from your ${getAppName(
        AppName.base,
        ' ',
        AppName.suffix3
      )}, from the My Health Tools section. Click “Launch Vynca” to enter the Vynca Portal.`
    ]
  },
  {
    categoryId: 'heading2',
    title: 'Overview of the paper process.',
    subtitle: [
      'Have a discussion with the person you want as your healthcare agent.',
      'Fill out your state’s advance directive paper form.',
      'Review your paper form for completeness and accuracy.',
      'Submit your advance directive to have it added to your medical record. (Refer to <a href="navigate-to-question/heading2" categoryid="heading2" title="I have completed a paper advance directive. How do I have it added to my medical record?">"I have completed a paper advance directive. How do I have it added to my medical record?"</a> for more information.)'
    ]
  },
  {
    categoryId: 'heading2',
    title: 'What are common mistakes made when filling out an advance directive?',
    subtitle: [
      'Technical errors or gaps (such as filling information out on the wrong line or in the wrong section).',
      'Vague statements that are hard to interpret (such as, “Don’t use any tubes to keep me alive,” “I don’t want to be a vegetable,” or “I don’t want any heroic measures”).',
      'Missing signatures and /or dates (date signed, or date of birth, for example).',
      'Choosing the wrong person to sign as a witness.'
    ]
  },
  {
    categoryId: 'heading2',
    title: 'Who cannot be a witness to an advance directive?',
    subtitle: [
      {
        text: [
          'Each state will be different. Some states require one or two witnesses, while others may require a witness and a notary.',
          'Those who <strong>cannot</strong> witness an advance directive are:'
        ],
        bullets: [
          'Those younger than 18 years old.',
          'The person named as the healthcare agent in the advance directive.',
          'A person related by blood or marriage to the individual filling out the advance directive.',
          'A person who may inherit from your estate.',
          'Any person responsible for your healthcare — financially or as a caregiver.'
        ]
      }
    ]
  },
  {
    categoryId: 'heading2',
    title:
      'How do I (or my healthcare agent) sign my advance directive form electronically (via the Vynca Portal)?',
    subtitle: [
      "Once prompted, select the icon “Connect Smart Device” from the Vynca menu bar. Enter the phone number where you would like the signature pad to be sent. A notification will be sent to the person's phone, and they will have the option to sign the document with a touch-screen device.",
      'The person signing will follow the prompts on their phone and sign their name using the touch keyboard.',
      'Once completed, the signature will be transferred to the document electronically.'
    ]
  },
  {
    categoryId: 'heading2',
    title:
      'I have completed a paper advance directive. How do I have it added to my medical record?',
    subtitle: [
      '<strong>Mail</strong> a copy to:',
      'Advance Directive at Intermountain Health',
      'PO Box #70539',
      'Salt Lake City, Utah 84170',
      {
        clickLink: {
          text: '<strong>Email</strong> a copy (as an attachment) to:&nbsp;',
          link: `AdvanceDirective@r1rcm.com`
        }
      },
      '<strong>Fax</strong> a copy to 801-442-0484.',
      "<strong>Bring</strong> a copy to your provider's office."
    ]
  },
  {
    categoryId: 'heading3',
    title: 'I just finished my form. What do I do now?',
    subtitle: [
      'Once completed, it is time to share your document with those who need it. (Refer to <a href="navigate-to-question/heading3" categoryid="heading3" title="Who should have a copy of my advance directive?">"Who should have a copy of my advance directive?"</a> for more information.)',
      'You may consider printing a copy to keep with your important documents.'
    ]
  },
  {
    categoryId: 'heading3',
    title: 'How do I share my document with my family and loved ones?',
    subtitle: [
      `Click on the advance directive card from your ${getAppName(
        AppName.base,
        ' ',
        AppName.suffix3
      )}, from the Health Tools section.`
    ]
  },
  {
    categoryId: 'heading3',
    title: 'Who should have a copy of my advance directive?',
    subtitle: [
      'Your primary care provider (such as your family doctor).',
      'Specialist providers on your care team (cardiologist, neurologist, nephrologist, etc.).',
      'Any family members who may need to know how to contact your agent in the event of an emergency.',
      'The individuals who you have chosen to be your alternate healthcare agents.',
      'Your closest hospital (wherever you would go for emergencies).',
      'Other medical facilities (such as hospitals or nursing homes) where you frequently receive treatment, or upon being admitted for a major procedure such as surgery.'
    ]
  },
  {
    categoryId: 'heading4',
    title: 'How do I make changes to my advance directive form?',
    subtitle: [
      `Click on the advance directive card from your ${getAppName(
        AppName.base,
        ' ',
        AppName.suffix3
      )}. The advance directive card is in the Health Tools section. Click “Launch Vynca” to enter the Vynca portal.`,
      'Click on the “My Documents” icon, found toward the top half of the page.',
      'Select the document you would like to edit or choose to create a new document.',
      'Once submitted, the new document will replace the older version.',
      'Verify that your new edits have been successfully submitted.'
    ]
  },
  {
    categoryId: 'heading4',
    title:
      'I gave my provider a paper copy of my advance directive form. Can I view it from the Vynca portal?',
    subtitle: [
      'Yes. Paper forms that have been submitted to medical records will be available to view and share within two to four business days. You will access the form by clicking the “My Documents” icon on the Vynca portal menu bar. Select the document you wish to view. (Refer to <a href="navigate-to-question/heading2" categoryid="heading2" title="How do I access the Vynca Portal?">"How do I access the Vynca Portal?"</a> for more information.)'
    ]
  },
  {
    categoryId: 'heading4',
    title: 'How do I view my advance directive?',
    subtitle: [
      'You will access the form by clicking the “My Documents” icon on the Vynca portal menu bar. Select the document you wish to view. (Refer to <a href="navigate-to-question/heading2" categoryid="heading2" title="How do I access the Vynca Portal?">"How do I access the Vynca Portal?"</a> for more information.)'
    ]
  },
  {
    categoryId: 'heading4',
    title: 'I do not see my completed advance directive form in My Documents. What should I do?',
    subtitle: [
      'When submitted manually, our medical records team reviews the completed document to ensure all legally required fields have been completed. If a field is missing, they will attempt to contact you to complete the missing portions. If you have not heard from them and it has been at least four days since the form was submitted, please contact your primary care provider.',
      'If you have completed an electronic version of the form, double check to make sure all required forms are complete and that you receive a message of success when you submit the form.'
    ]
  }
];
